import { useQuery } from "@tanstack/react-query";
import { getApiClient } from "../api-client";

export interface PublicGatheringSummary {
  id: string;
  title: string;
  date: string;
  endDate: string | null;
  neighborhood: string | null;
  city: string | null;
  capacity: number | null;
  rsvpCount: number;
  club: { id: string; name: string };
}

export interface PublicGatheringDetail {
  id: string;
  title: string;
  description: string | null;
  date: string;
  endDate: string | null;
  venueType: string | null;
  venueName: string | null;
  neighborhood: string | null;
  address: string | null;
  city: string | null;
  capacity: number | null;
  rsvpCount: number;
  /**
   * True when the gathering's start date has passed. The page still renders
   * (so existing indexed URLs do not 404), but it sets
   * <meta name="robots" content="noindex"> to drop the URL from search.
   */
  isPast: boolean;
  club: { id: string; name: string; description: string | null };
}

interface PublicGatheringsResponse {
  gatherings: PublicGatheringSummary[];
}

export const publicGatheringsKey = ["public", "gatherings"] as const;
export const publicGatheringDetailKey = (id: string) =>
  ["public", "gatherings", id] as const;

/**
 * Lists future, publicly-visible gatherings across all discoverable clubs.
 * Unauthenticated — the underlying endpoint is exposed in the `/public/*`
 * namespace and the server applies the visibility gate.
 */
export function usePublicGatherings() {
  return useQuery<PublicGatheringSummary[]>({
    queryKey: publicGatheringsKey,
    queryFn: async () => {
      const res =
        await getApiClient().get<PublicGatheringsResponse>(
          "/public/gatherings",
        );
      return res.gatherings;
    },
    staleTime: 60 * 1000,
  });
}

/**
 * Fetches a single public gathering by id. The endpoint 404s when the
 * gathering is not publicly visible OR has been cancelled. Past gatherings
 * still render so existing indexed URLs do not 404 — they're flagged with
 * `isPast: true` so the page can apply <meta name="robots" content="noindex">.
 */
export function usePublicGatheringDetail(id: string) {
  return useQuery<PublicGatheringDetail>({
    queryKey: publicGatheringDetailKey(id),
    queryFn: async () =>
      getApiClient().get<PublicGatheringDetail>(`/public/gatherings/${id}`),
    staleTime: 60 * 1000,
    retry: false,
  });
}
